/*
  Flowkeeper - Pomodoro timer for power users and teams
  Copyright (c) 2023 Constantine Kulak, Geoffrey Espagne

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU General Public License as published by
  the Free Software Foundation; either version 3 of the License, or
  (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU General Public License for more details.

  You should have received a copy of the GNU General Public License
  along with this program.  If not, see <https://www.gnu.org/licenses/>.
*/

import {useContext, useEffect, useState, FormEvent} from 'react';
import {
    Button,
    Checkbox, 
    Modal, 
    ModalVariant,
} from '@patternfly/react-core';
import {Flex, FlexItem} from '@patternfly/react-core';
import {useNavigate, useLocation} from "react-router-dom";
import {sendTokenRequest} from "../../fk/core/token_services"

import {disconnectEventSource, initEventSource} from "../../fk/core/websocket_event_source";

import DataContext from '../context/DataContext';
import ConnectionContext from '../context/ConnectionContext';

const Cookies = require("js-cookie");

type ConsentPageProps = {
    from: string
}

ConsentPage.defaultProps = {
    from: 'google'
}

function ConsentPage(props: ConsentPageProps) {

    const [isConsentChecked, setIsConsentChecked] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(true);

    const { data, setData } = useContext(DataContext);
    const { setConnection } = useContext(ConnectionContext);

    const handleModalToggle = (_event: KeyboardEvent | React.MouseEvent) => {
        setIsModalOpen(!isModalOpen);
    };

    const handleClose = (_event: KeyboardEvent | React.MouseEvent) => {
        Cookies.remove('fw-utoken');
        Cookies.remove('fw-uauthtype');
        Cookies.remove('fw-uavatar');
        setIsModalOpen(!isModalOpen);
    };

    const navigate = useNavigate();

    const {state} = useLocation();
    let authType: string = '';
    if (state != null) {
        authType = state.from;
    }
    if (!authType) {
        authType = props.from;
    }

    const handleConsentCheckboxChange = (_event: FormEvent, checked: boolean) => {
        setIsConsentChecked(checked);
    };

    const onAccept = () => {
        if (!data) {
            throw new Error("Trying to provide consent when there's no data");
        }
        switch (authType) {
            case 'basic':
                navigate("/")
                break;
            case 'google':
                data.needConsent = false;
                disconnectEventSource(setConnection, setData);
                sendTokenRequest().then(response => {
                    let tokens = response[0];
                    let decodedIdToken = response[1];

                    let avatar;
                    if (decodedIdToken.picture) {
                        avatar = decodedIdToken.picture;
                        Cookies.set('fw-uavatar', avatar, {expires: 30});
                    } else {
                        avatar = Cookies.get('fw-uavatar');
                    }

                    initEventSource(setData,
                        setConnection,
                        decodedIdToken.email,
                        tokens.id_token,
                        authType,
                        true);
                  })
                  .catch((error => {
                    console.error("Token request error", error);
                  }));         
                break;
            default:
                throw new Error("Unsupported auth type: " + authType);
        }
        setIsModalOpen(!isModalOpen);
    }

    useEffect(() => {
        if (data && data.isLoggedIn) {
            navigate("/");
        }
    }, [data]);

    return (
        <Modal
            variant={ModalVariant.medium}
            title="Important disclaimer, read carefully"
            titleIconVariant="warning"
            isOpen={isModalOpen}
            onClose={handleModalToggle}
            actions={[
                <Button isDisabled={!isConsentChecked} key="confirm" variant="primary" onClick={onAccept}>
                    Create account
                </Button>,
                <Button key="cancel" variant="link" onClick={handleClose}>
                    Decline
                </Button>
            ]}
        >
            <Flex>
                <FlexItem>Flowkeeper.org is a free service, which comes with no warranty. Use it on your own risk.
                    It is in its early BETA
                    testing stage. Most importantly, <strong>it lacks end-to-end encryption</strong>, which means that our engineers
                    will have access to your data. This service acts as a testing playground for Flowkeeper desktop app.
                    YOU SHOULD NOT STORE ANY SENSITIVE INFORMATION IN FLOWKEEPER.ORG at this stage.
                </FlexItem>
                <FlexItem><Checkbox isChecked={isConsentChecked}
                                    label="I understand that my data will not be properly protected and may permanently disappear at any moment without notice."
                                    id="consent-check"
                                    onChange={handleConsentCheckboxChange}
                /></FlexItem>
            </Flex>
        </Modal>
    );
}

export default ConsentPage;
